declare global {
  interface Window {
    webkit: {
      messageHandlers: {
        [functionName: string]: {
          postMessage(paramsJson: string): void;
        };
      };
    };

    DD_RUM: {
      getInternalContext(): {session_id: string};
      setGlobalContextProperty(key: string, value: string): void;
      setUser({id}: {id: string}): void;
    };

    [interfaceName: string]: {
      [functionName: string]: (paramsJson: string) => void;
    };

    [K: `appCallback_${string}`]: ((response: string) => void) | null;

    openBottomSheetWebViewCallback(): void;
  }
}

const INTERFACE_NAME = 'HMGCommonInterface';

const INTERFACE_FUNCTION = {
  LOADING: 'loading',
  SHOW_TOAST: 'showToast',
  OPEN_SHARE: 'openShare',
  OPEN_CUSTOM_ALERT: 'openCustomAlert',
  OPEN_BOTTOM_SHEET: 'openBottomSheet',
  OPEN_BOTTOM_SHEET_WEBVIEW: 'openBottomSheetWebView',
  SET_BOTTOM_SHEET_WEBVIEW_SIZE: 'setBottomSheetWebViewSize',
  OPEN_NEW_WEB_VIEW: 'openNewWebView',
  OPEN_WEB_BROWSER: 'openWebBrowser',
  OPEN_IMAGE_VIEWER: 'openImageViewer',
  CLOSE: 'close',
  HISTORY_BACK: 'historyBack',
  UPDATE_NAVIGATION_BAR_TITLE: 'updateNavigationBarTitle',
  NAVIGATION_BAR: 'navigationBar',
  SYSTEM_BACKGROUND_COLOR: 'systemBackgroundColor',
  SYSTEM_STATUS_BAR: 'systemStatusBar',
  SCREEN_ORIENTATION: 'screenOrientation',
  LOGOUT: 'logout',
  DOWNLOAD_URL: 'downloadUrl',
  NAVIGATION_BACK_GESTURE: 'navigationBackGesture',
  SCROLL_BOUNCE: 'scrollBounce',
  SYSTEM_SAFE_AREA: 'systemSafeArea',
  FULL_SCREEN: 'fullScreen',
  PULL_TO_REFRESH: 'pullToRefresh',
  OPEN_SHARE_LOCATION: 'openShareLocation',
  SCREEN_BRIGHTNESS: 'screenBrightness',
  SHOW_COPY_TOAST: 'showCopyToast',
} as const;

export interface OpenCustomAlertProps {
  title: string;
  message: string;
  isVerticalButton?: boolean;
  isCancellable?: boolean;
  buttons: Array<ButtonsType>;
}

export interface OpenBottomSheetProps {
  type: BottomSheetSelectType;
  selectedIndexes: Array<number>;
  title?: string;
  message?: string;
  items: Array<SelectItemType>;
  buttons?: Array<ButtonsType>;
  isCancellable?: boolean;
}

export interface OpenImageViewerProps {
  type: keyof typeof VIEWER_TYPE;
  startIndex: number;
  selectedIndexes: Array<number>;
  items: Array<ImageItemType>;
}

export interface OpenNewWebviewProps {
  url: string;
  isNavigationBar?: boolean;
  isToolBar?: boolean;
  title?: string;
  isModal?: boolean;
}

export interface ButtonsType {
  text: string;
  style: keyof typeof BUTTON_STYLE_TYPE | string;
  actionType: keyof typeof BUTTON_ACTION_TYPE;
}

export interface SelectItemType {
  text: string;
  icon: string;
}

export interface ImageItemType {
  text: string;
  imageUrl: string;
}

export interface BottomSheetResponseType {
  uid: string;
  selectedIndexes: Array<number>;
  selectedButtonIndex?: number;
}

export interface OpenImageViewerResponseType {
  uid: string;
  selectedIndexes: Array<number>;
}

export interface OpenCustomAlertResponseType {
  uid: string;
  selectedIndex: number;
}

export interface FullScreenPadding {
  /**
   * 상단 영역(인디케이터, 노치영역)
   */
  topHeight: string;
  /**
   * 하단 영역(컨트롤 영역)
   */
  bottomHeight: string;
}

export interface FullScreenResponseType extends FullScreenPadding {
  uid: string;
}

export interface GetEnvironmentProps {
  appName: string;
  userAgent: string;
  locale: string | null;
  region?: string | null;
  lang: string | null;
  brand?: string | null;
  theme?: string | null;
  speedUnit?: string | null;
  distanceUnit?: string | null;
  timezone: string;
}

export interface EnvironmentType {
  appName: string;
  os: keyof typeof USER_OS_TYPE;
  userAgent: string;
  locale: string | null;
  lang: string | null;
  brand?: string | null;
  theme?: string | null;
  isWebview: boolean;
  appVersion?: string | null;
  packageID?: string | null;
}

export interface DownloadUrlProps {
  fileName: string;
  url: string;
  mimeType: string;
  isOpenShare?: boolean;
  messages?: {
    complete: string;
    failure: string;
  };
}

export interface ScreenBrightnessResponseType {
  uid: string;
  prevBrightness: number;
}

export const DURATION_TIME_TYPE = {
  SHORT: 'SHORT',
  LONG: 'LONG',
} as const;

export const VIEWER_TYPE = {
  VIEWER: 'VIEWER',
  SINGLE_SELECT: 'SINGLE_SELECT',
  MULTIPLE_SELECT: 'MULTIPLE_SELECT',
} as const;

export enum BottomSheetSelectType {
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
}

export const BUTTON_STYLE_TYPE = {
  BASIC: 'BASIC',
  PRIMARY: 'PRIMARY',
  UNDERLINE: 'UNDERLINE',
} as const;

export const BUTTON_ACTION_TYPE = {
  DEFAULT: 'DEFAULT',
  CANCEL: 'CANCEL',
  DESTRUCTIVE: 'DESTRUCTIVE',
} as const;

export const SYSTEM_STYLE_TYPE = {
  SYSTEM: 'SYSTEM',
  LIGHT: 'LIGHT',
  DARK: 'DARK',
} as const;

export const SCREEN_ORIENTATION_TYPE = {
  SYSTEM: 'SYSTEM',
  PORTRAIT: 'PORTRAIT',
  LANDSCAPE: 'LANDSCAPE',
} as const;

export const USER_OS_TYPE = {
  IOS: 'IOS',
  AOS: 'AOS',
  WEB: 'WEB',
} as const;

let id = 0;

function getId() {
  id += 1;
  return `${id}`;
}

export function callInterface<T>(interfaceName: string, functionName: string, params: T, global: Window = window) {
  const paramsJson = JSON.stringify(params);

  console.log(`[Webview] interfaceName:${interfaceName} functionName:${functionName} params:${paramsJson}`);
  global?.webkit?.messageHandlers?.[`${interfaceName}_${functionName}`]?.postMessage(paramsJson);
  global?.[interfaceName]?.[functionName]?.(paramsJson);
}

export function commonCallInterface<T>(functionName: string, params: T, global: Window = window) {
  callInterface(INTERFACE_NAME, functionName, params, global);
}

function loading(isShow: boolean) {
  commonCallInterface(INTERFACE_FUNCTION.LOADING, {isShow});
}

function showToast(message: string, durationTime: keyof typeof DURATION_TIME_TYPE = DURATION_TIME_TYPE.SHORT) {
  commonCallInterface(INTERFACE_FUNCTION.SHOW_TOAST, {message, durationTime});
}

function openShare(content: string) {
  commonCallInterface(INTERFACE_FUNCTION.OPEN_SHARE, {content});
}

function openShareLocation({latitude, longitude}: {latitude: number; longitude: number}) {
  commonCallInterface(INTERFACE_FUNCTION.OPEN_SHARE_LOCATION, {latitude, longitude});
}

function openCustomAlert({title, message, isVerticalButton = true, isCancellable = true, buttons}: OpenCustomAlertProps) {
  return new Promise<number>((resolve) => {
    const uid = getId();
    const callbackFuncName = `appCallback_${uid}` as const;

    window[callbackFuncName] = function (response: string) {
      const params: OpenCustomAlertResponseType = JSON.parse(response);
      if (resolve) {
        resolve(params.selectedIndex);
        window[callbackFuncName] = null;
      }
    };

    commonCallInterface(INTERFACE_FUNCTION.OPEN_CUSTOM_ALERT, {
      contents: {title, message, isVerticalButton, isCancellable, buttons},
      uid,
      resultFuncName: callbackFuncName,
    });
  });
}

function openBottomSheet({isCancellable = true, ...rest}: OpenBottomSheetProps) {
  return new Promise<BottomSheetResponseType>((resolve) => {
    const uid = getId();
    const callbackFuncName = `appCallback_${uid}` as const;

    window[callbackFuncName] = function (response: string) {
      const params: BottomSheetResponseType = JSON.parse(response);
      if (resolve) {
        resolve(params);
        window[callbackFuncName] = null;
      }
    };

    commonCallInterface(INTERFACE_FUNCTION.OPEN_BOTTOM_SHEET, {
      contents: {...rest, isCancellable},
      uid,
      resultFuncName: callbackFuncName,
    });
  });
}

function openBottomSheetWebView() {
  return new Promise<void>((resolve) => {
    const readyFuncName = 'openBottomSheetWebViewCallback';
    window[readyFuncName] = () => {
      resolve();
    };
    commonCallInterface(INTERFACE_FUNCTION.OPEN_BOTTOM_SHEET_WEBVIEW, {
      readyFuncName,
    });
  });
}

function setBottomSheetWebViewSize(height: number, global: Window) {
  commonCallInterface(INTERFACE_FUNCTION.SET_BOTTOM_SHEET_WEBVIEW_SIZE, {height}, global);
}

function openImageViewer(props: OpenImageViewerProps) {
  return new Promise<Array<number>>((resolve) => {
    const uid = getId();
    const callbackFuncName = `appCallback_${uid}` as const;

    window[callbackFuncName] = function (response: string) {
      const params: OpenImageViewerResponseType = JSON.parse(response);
      if (resolve) {
        resolve(params.selectedIndexes);
        window[callbackFuncName] = null;
      }
    };
    commonCallInterface(INTERFACE_FUNCTION.OPEN_IMAGE_VIEWER, {
      contents: {...props},
      uid,
      resultFuncName: callbackFuncName,
    });
  });
}

function openNewWebView({url, isNavigationBar = true, isToolBar = true, isModal = false}: OpenNewWebviewProps) {
  commonCallInterface(INTERFACE_FUNCTION.OPEN_NEW_WEB_VIEW, {url, isNavigationBar, isToolBar, isModal});
}

function openWebBrowser(url: string, isInApp: boolean = false) {
  commonCallInterface(INTERFACE_FUNCTION.OPEN_WEB_BROWSER, {url, isInApp});
}

function close(nextScheme?: string) {
  commonCallInterface(INTERFACE_FUNCTION.CLOSE, {nextScheme});
}

/**
 * 앱이 해당 스킴을 받으면, history.back 처리. 만약 뒤로 갈 히스토리가 없다면 close 처리.
 * 웹뷰에서는 뒤로가기 시 반드시 본 스킴을 사용해야함
 *
 */
function historyBack() {
  commonCallInterface(INTERFACE_FUNCTION.HISTORY_BACK, {});
}

function updateNavigationBarTitle(title: string) {
  document.title = title;
  commonCallInterface(INTERFACE_FUNCTION.UPDATE_NAVIGATION_BAR_TITLE, {});
}

function navigationBar(isShow: boolean) {
  commonCallInterface(INTERFACE_FUNCTION.NAVIGATION_BAR, {isShow});
}

function systemBackgroundColor(top: string, bottom: string) {
  commonCallInterface(INTERFACE_FUNCTION.SYSTEM_BACKGROUND_COLOR, {top, bottom});
}

function systemStatusBar(style: keyof typeof SYSTEM_STYLE_TYPE) {
  commonCallInterface(INTERFACE_FUNCTION.SYSTEM_STATUS_BAR, {style});
}

function screenOrientation(orientation: keyof typeof SCREEN_ORIENTATION_TYPE = SCREEN_ORIENTATION_TYPE.SYSTEM) {
  commonCallInterface(INTERFACE_FUNCTION.SCREEN_ORIENTATION, {orientation});
}

function logout(nextScheme: string) {
  commonCallInterface(INTERFACE_FUNCTION.LOGOUT, {nextScheme});
}

function downloadUrl(props: DownloadUrlProps) {
  commonCallInterface(INTERFACE_FUNCTION.DOWNLOAD_URL, {
    ...props,
  });
}

function navigationBackGesture(isEnable: boolean) {
  commonCallInterface(INTERFACE_FUNCTION.NAVIGATION_BACK_GESTURE, {isEnable});
}

function scrollBounce(isEnable: boolean) {
  commonCallInterface(INTERFACE_FUNCTION.SCROLL_BOUNCE, {isEnable});
}

function systemSafeArea(isEnable: boolean = true) {
  commonCallInterface(INTERFACE_FUNCTION.SYSTEM_SAFE_AREA, {isEnable});
}

/**
 * iOS에서 상태 바, 하단 컨트롤 바 영역까지 영역을 확장할 수 있습니다.
 * *Android 에서도 충전/정비 메인 화면에서 필요해서 추가했습니다.
 * @param top true: 상단 영역이 fullScreen으로 확장됩니다. 리턴해주는 topHeight 만큼 콘텐츠에 패딩이 추가되어야합니다.
 * @param bottom true: 하단 영역이 fullScreen 으로 확장됩니다. 리턴해주는 bottomHeight 만큼 콘텐츠에 패딩이 추가되어야합니다.
 */
function fullScreen(top: boolean, bottom: boolean) {
  return new Promise<FullScreenResponseType>((resolve) => {
    const uid = getId();
    const callbackFuncName = `appCallback_${uid}` as const;

    window[callbackFuncName] = function (response: string) {
      const params: FullScreenResponseType = JSON.parse(response);
      if (resolve) {
        resolve(params);
        window[callbackFuncName] = null;
      }
    };

    commonCallInterface(INTERFACE_FUNCTION.FULL_SCREEN, {
      contents: {top, bottom},
      uid,
      resultFuncName: callbackFuncName,
    });
  });
}

function pullToRefresh(isEnable: boolean = true) {
  commonCallInterface(INTERFACE_FUNCTION.PULL_TO_REFRESH, {isEnable});
}

function getEnvironment({
  locale = null,
  region = null,
  lang = null,
  brand = null,
  theme = null,
  userAgent,
  appName,
  distanceUnit = null,
  speedUnit = null,
  timezone,
}: GetEnvironmentProps) {
  let os: keyof typeof USER_OS_TYPE;

  // OS 세팅
  if (userAgent.match(/(iPad|iPhone|iPod)/i)) {
    os = USER_OS_TYPE.IOS;
  } else if (userAgent.match(/android/i)) {
    os = USER_OS_TYPE.AOS;
  } else {
    os = USER_OS_TYPE.WEB;
  }

  const regexString = `(${appName})(?:\\/([^ ]*))? \\(([^)]+)\\)`;
  const regex = new RegExp(regexString);
  const matches = regex.exec(userAgent);

  if (matches) {
    // APP 에서 준 값이 있다면 파싱
    const [, , appVersion, pairText] = matches;

    const pairs = pairText.split(',').reduce(
      (result, pair) => {
        const [key, value] = pair.split('=');
        result[key] = value.trim();
        return result;
      },
      {} as Record<string, string>
    );

    return {
      os,
      appVersion,
      userAgent,
      appName,
      locale: pairs.locale?.toUpperCase() || locale,
      region: pairs.region?.toUpperCase() || region,
      brand: pairs.brand?.toUpperCase() || brand,
      theme: pairs.theme?.toUpperCase() || theme,
      lang: pairs.lang,
      isWebview: true,
      speedUnit: pairs.speedUnitType?.toUpperCase() || speedUnit,
      distanceUnit: pairs.distanceUnitType?.toUpperCase() || distanceUnit,
      timezone: pairs.timezone || timezone,
    };
  }
  return {
    os,
    userAgent,
    appName,
    locale,
    region,
    lang,
    brand,
    theme,
    isWebview: false,
    speedUnit,
    distanceUnit,
    timezone,
  };
}

/**
 * @param brightness : 화면 밝기, 0~1, 1이 가장 밝음
 */
function setScreenBrightness(brightness: number) {
  return new Promise<ScreenBrightnessResponseType>((resolve) => {
    const uid = getId();
    const callbackFuncName = `appCallback_${uid}` as const;

    window[callbackFuncName] = function (response: string) {
      const params: ScreenBrightnessResponseType = JSON.parse(response);

      if (resolve) {
        resolve(params);
        window[callbackFuncName] = null;
      }
    };

    commonCallInterface(INTERFACE_FUNCTION.SCREEN_BRIGHTNESS, {
      contents: {brightness},
      uid,
      resultFuncName: callbackFuncName,
    });
  });
}

/**
 * showCopyToast 안드로이드 only, OS13을 기준으로 시스템 토스트를 노출 하기에 해당 인터페이스로 안드로이드 대응하면 OS에 따라 처리
 */
function showCopyToast({
  message,
  durationTime = DURATION_TIME_TYPE.SHORT,
}: {
  message: string;
  durationTime?: keyof typeof DURATION_TIME_TYPE;
}) {
  commonCallInterface(INTERFACE_FUNCTION.SHOW_COPY_TOAST, {message, durationTime});
}

export const WebviewUtils = {
  commonCallInterface,
  getEnvironment,
  loading,
  showToast,
  openShare,
  openCustomAlert,
  openBottomSheet,
  openBottomSheetWebView,
  setBottomSheetWebViewSize,
  openImageViewer,
  openNewWebView,
  openWebBrowser,
  close,
  historyBack,
  updateNavigationBarTitle,
  navigationBar,
  systemBackgroundColor,
  systemStatusBar,
  screenOrientation,
  logout,
  downloadUrl,
  navigationBackGesture,
  scrollBounce,
  systemSafeArea,
  fullScreen,
  pullToRefresh,
  openShareLocation,
  setScreenBrightness,
  showCopyToast,
};
